import MainLayout from "@/layouts/mainLayout";
import Head from "next/head";
import styles from "@/styles/AboutUs.module.css";
import ActivityStyles from "@/styles/Activity.module.css";
import axios from "@/serve/axios";


import { GetServerSidePropsContext } from "next";
import { Pagination } from "antd";
import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import ListColumnItem from "@/components/ListColumnItem";
import websiteAxios from "@/serve/websiteAxios";
import Nav from "@/components/nav";
import useBrowse from "@/hooks/useBrowse";
import type { NextPageWithLayout } from "@/types/App";

// ----------------------------------------文章列表------------------------------------------
export async function getServerSideProps(context: GetServerSidePropsContext) {
    context.res.setHeader(
        "Cache-Control",
        "public, s-maxage=10, stale-while-revalidate=59"
    );
    // 党建园地的 columnId 和 channelId 固定写死的
    const channelId = 6;
    const columnId = 97;

    const navigationData = await axios.post<null, Nav[]>("/website/index/navigation");
    const websiteInfo = await axios.post("/website/siteInfo")

    // 获取文章列表
    let data: Base.Page<Article> = await axios.post<null, Base.Page<Article>>(
        "/website/article/page?pageIndex=1&pageSize=10",
        { channelId, columnId }
    );

    return {
        props: {
            navigationData,
            data,
            columnId,
            channelId,
            websiteInfo
        },
    };
}

// -------------------------------------------文章列表---------------------------------------
type Props = {
    data: Base.Page<Article>;
    channelId: string;
    columnId: string;
} & WebsiteAppProps;

const Building: NextPageWithLayout<Props> = (props) => {
    const { channelId, columnId } = props;
    const [data, setData] = useState<Article[]>([]);
    const [pageIndex, setPageIndex] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);

    const getData = useCallback(async (pageIndex: number, pageSize: number) => {
        websiteAxios.post<any, Base.Page<Article>>(
            `/api/database_services?endpoint=articlePage&pageIndex=${pageIndex}&pageSize=${pageSize}`, { channelId, columnId }
        )
            .then((data) => {
                setTotal(data.total);
                setData([...data.records]);
            }).catch((err) => {
                console.error('err', err)
            })
    }, [channelId, columnId]);

    function onChange(page: number, size: number) {
        setPageIndex(page);
        setPageSize(size)
        getData(page, size);
    }

    useEffect(() => {
        if (!props.data || !props.data?.records || !props.data.records.length) return;
        setPageIndex(1);
        setTotal(props.data.total);
        // 取props.data的后面的数据
        setData(props.data.records);
    }, [props.data]);

    // 埋点点击 手动触发
    useBrowse({ url: '/api/tracking', data: { dataType: 35, thirdChannelId: 4 } });

    const items = [
        {
            key: '1',
            name: "关于我们",
            href: "/about",
        },
        {
            key: '2',
            name: "关于学会",
            href: "/about/society",
        },
        {
            key: '3',
            name: "架构及章程",
            href: "/about/charter",
        },
        {
            key: '4',
            name: "党建园地",
            href: "/about/building",
        },
        {
            key: '5',
            name: "工作机会",
            href: "/about/job",
        },
        {
            key: '6',
            name: "联系我们",
            href: "/about/contact",
        },
    ];

    return (
        <>
            {/* 二级导航 */}
            <Nav items={items} />
            <article>
                <section
                    className={styles.banner}
                    style={{
                        backgroundImage: `url(https://hjblm-platform.chard.org.cn/rare_disease/image/20233/about_banner.jpg)`,
                    }}
                >
                    <div className={styles.banner__content}>
                        <h1>党建园地</h1>
                        <p>因为有爱，所以同行！</p>
                    </div>
                </section>
            </article>

            {props.data.records.length > 0 ? (
                <article
                    className={classNames(ActivityStyles.activity, ActivityStyles.activity_wrapper)}
                >
                    {data.map((item) => (
                        <ListColumnItem
                            key={item.id}
                            title={item.articleName}
                            content={item.articleAbstract}
                            type="building"
                            id={item.id}
                            friendlyTime={item.friendlyTime}
                        />
                    ))}
                    <div className="pagination">
                        <Pagination
                            current={pageIndex}
                            total={total}
                            pageSize={pageSize}
                            showLessItems={false}
                            onChange={onChange}
                            itemRender={(page, type, originalElement) => {
                                if (type === "prev") {
                                    return (
                                        <a href="#" title="下一页">
                                            上一页
                                        </a>
                                    );
                                }
                                if (type === "next") {
                                    return (
                                        <a href="#" title="下一页">
                                            下一页
                                        </a>
                                    );
                                }
                                if (type === "page") {
                                    return (
                                        <a href="#" title="页码">
                                            {page}
                                        </a>
                                    );
                                }
                                return originalElement;
                            }}
                        />
                    </div>
                </article>
            ) : (
                <article className={ActivityStyles.no_data}>暂无数据</article>
            )}
        </>
    );
}

Building.getLayout = (Page, layoutProps, pageProps) => {
    return (
        <>
            <Head>
                <title>党建园地</title>
                <meta property="og:title" content="党建园地"></meta>
                <meta
                    name="keywords"
                    content="中国罕见病联盟,诊疗培训,罕见病新进展,罕见病学术资讯,患者管理,医学运维,教育培训服务,综合信息服务,医生诊疗能力提升,国际罕见病日系列活动,中国罕见病大会,罕见病领域权威会议,病友故事,关爱使者,医患教育,科普内容,联盟活动,直播活动,病例征集活动"
                />
                <meta
                    name="description"
                    content="中国罕见病联盟是经国家卫健委医政医管局同意，北京协和医院、中国医药创新促进会、中国医院协会、中国研究型医院学会牵头发起，由具有罕见病诊疗能力的医疗机构、科研院所、患者组织、医药企业等主体自愿联合组成的全国性、非营利性、合作性的交流平台。"
                />
                <meta
                    property="og:description"
                    content="中国罕见病联盟是经国家卫健委医政医管局同意，北京协和医院、中国医药创新促进会、中国医院协会、中国研究型医院学会牵头发起，由具有罕见病诊疗能力的医疗机构、科研院所、患者组织、医药企业等主体自愿联合组成的全国性、非营利性、合作性的交流平台。"
                />
                <meta name="robots" content="index,nofollow" />
                <meta property="og:locale" content="zh_CN" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="中国罕见病综合云服务平台" />
                <meta name="renderer" content="webkit" />
                <meta httpEquiv="X-UA-Compatible" content="chrome=1" />
                <link rel="canonical" href="https://www.chard.org.cn/about/building" />
                <meta property="og:url" content="https://www.chard.org.cn/about/building" />
                <meta property="og:image" content="https://hjblm-platform.oss-cn-beijing.aliyuncs.com/rare_disease/2023/11/20231120-112022.jpg" />
            </Head>
            <h1 className="visually_hidden" aria-level={1}>中国罕见病综合云服务平台官方网站 - 党建园地页面</h1>
            <MainLayout {...layoutProps}>
                <Page {...pageProps} />
            </MainLayout>
        </>
    );
};

export default Building;
